/**
 * Static MOE secondary-stream syllabus topic tables (G1/G2/G3/IP) and
 * secondary-level framing context. Imported only by the secondary-stream
 * generator, so it lands in the SecondaryStreamPage chunk — not on primary or
 * exam landing pages.
 */
import type { SyllabusTopic } from './types';

/**
 * MOE G1 Mathematics topics by secondary level (Sec 1–4).
 * Used for Secondary 1–4 G1 stream pages. Update when syllabus changes.
 *
 * Uses SyllabusTopic[] for levels that surface topic groupings and
 * syllabus-backed subpoints on the public stream pages.
 */
export const G1_TOPICS_BY_LEVEL: Record<number, string[] | SyllabusTopic[]> = {
  1: [
    {
      category: 'Numbers',
      items: [
        'Negative numbers and primes, excluding prime factorisation',
        'Representing and ordering numbers on the number line',
        'Using <, >, <=, and >= to compare numbers',
        'Approximation and estimation, including rounding and significant figures',
      ],
    },
    {
      category: 'Four Operations',
      items: [
        'Integer operations: addition, subtraction, multiplication, and division',
        'Fraction and decimal operations, including negative fractions and decimals',
        'Calculator work with squares, cubes, square roots, and cube roots',
      ],
    },
    {
      category: 'Ratio',
      items: [
        'Comparing two or more quantities by ratio',
        'Dividing a quantity in a given ratio',
        'Equivalent ratios and ratios in simplest form',
        'Ratios involving fractions and decimals',
      ],
    },
    {
      category: 'Percentage',
      items: [
        'Converting between percentages, fractions, and decimals',
        'Finding the whole from a percentage part',
        'Comparing quantities by percentage, including percentages greater than 100%',
        'Percentage increase/decrease and increasing/decreasing by a given percentage',
      ],
    },
    {
      category: 'Algebra',
      items: [
        'Using letters to represent numbers and reading common algebra notation',
        'Evaluating algebraic expressions and formulae',
        'Recognising number sequences, including simple nth terms such as n + 3 and 2n + 1',
        'Translating simple real-world situations into algebraic expressions',
      ],
    },
    {
      category: 'Angles',
      items: [
        'Right, acute, obtuse, and reflex angles',
        'Vertically opposite angles, angles on a straight line, and angles at a point',
        'Corresponding, alternate, and interior angles with parallel lines',
      ],
    },
    {
      category: 'Statistics',
      items: [
        'Collecting, classifying, and tabulating data',
        'Reading tables, bar graphs, pictograms, line graphs, and pie charts',
        'Comparing the purposes, uses, advantages, and disadvantages of different representations',
      ],
    },
    {
      category: 'Symmetry',
      items: [
        'Line and rotational symmetry of plane figures',
        'Identifying lines of symmetry',
        'Finding the order of rotational symmetry',
      ],
    },
    {
      category: 'Area and Perimeter',
      items: [
        'Area of triangles, parallelograms, and trapeziums',
        'Area and circumference of circles',
        'Perimeter and area of composite plane figures',
      ],
    },
    {
      category: 'Surface Area and Volume',
      items: [
        'Volume and surface area of cubes and cuboids',
        'Converting between cm² and m², and between cm³ and m³',
        'Volume and surface area of composite solids',
      ],
    },
  ],
  2: [
    {
      category: 'Triangles and Quadrilaterals',
      items: [
        'Properties of triangles and special quadrilaterals',
        'Perpendicular bisectors, angle bisectors, and simple geometric constructions',
      ],
    },
    {
      category: 'Linear Algebraic Expressions',
      items: [
        'Adding, subtracting, and simplifying linear expressions',
        'Working with brackets and fractional coefficients in expressions',
      ],
    },
    {
      category: 'Linear Equations in One Variable',
      items: [
        'Solving linear equations in one unknown',
        'Forming linear equations to represent and solve word problems',
      ],
    },
    {
      category: 'Functions and Graphs',
      items: [
        'Cartesian coordinates and ordered-pair relationships',
        'Linear functions, linear graphs, and positive or negative gradients',
      ],
    },
    {
      category: 'Proportion',
      items: ['Direct and inverse proportion in numerical contexts'],
    },
    {
      category: 'Rate and Speed',
      items: [
        'Rates, average rates, speed, and average speed',
        'Unit conversion such as km/h to m/s',
      ],
    },
    {
      category: 'Mathematics in Practical Situations',
      items: [
        'Integrated application of proportion, rate, speed, and linear equations in everyday contexts',
      ],
    },
    {
      category: 'Congruence and Similarity',
      items: [
        'Congruent and similar figures',
        'Equal corresponding angles and proportional corresponding sides',
      ],
    },
    {
      category: "Pythagoras' Theorem",
      items: [
        "Using Pythagoras' theorem",
        'Checking whether a triangle is right-angled from its three side lengths',
      ],
    },
    {
      category: 'Data Analysis',
      items: [
        'Dot diagrams and histograms with equal class intervals',
        'Choosing suitable statistical representations and interpreting their trade-offs',
        'Mean, mode, and median for ungrouped data',
      ],
    },
    {
      category: 'Probability',
      items: [
        'Probability as a measure of chance',
        'Single-event probability by listing possible outcomes',
      ],
    },
    {
      category: 'Prisms and Cylinders',
      items: [
        'Volume and surface area of prisms and cylinders',
        'Area and volume unit conversions between cm², m², cm³, and m³',
      ],
    },
  ],
  3: [
    {
      category: 'Indices and Standard Form',
      items: [
        'Index notation for integer powers',
        'Standard form A × 10ⁿ, where n is an integer and 1 ≤ A < 10',
      ],
    },
    {
      category: 'Map Scales',
      items: ['Map scales involving distance and area'],
    },
    {
      category:
        'Algebraic Expansion, Fractions, and Expression for the nth Term of a Number Sequence',
      items: [
        'Recognising and representing number sequences',
        'Finding algebraic expressions for simple nth terms such as n + 3 and 2n + 1',
        'Expanding the product of two linear expressions',
        'Multiplying and dividing simple algebraic fractions',
      ],
    },
    {
      category: 'Factorisation and Formulas',
      items: [
        'Changing the subject of a simple formula',
        'Finding the value of an unknown quantity in a given formula',
        'Factorising linear expressions of the form ax + kay',
        'Factorising quadratic expressions of the form x² + px + q',
      ],
    },
    {
      category: 'Arc Lengths and Sector Areas',
      items: [
        'Arc length as a fraction of the circumference of a circle',
        'Sector area as a fraction of the area of a circle',
      ],
    },
    {
      category: 'Pyramids, Cones and Spheres',
      items: [
        'Volume and surface area of pyramids, cones, and spheres',
        'Conversions between cm² and m², and between cm³ and m³',
      ],
    },
    {
      category: 'Cumulative Frequency',
      items: [
        'Percentiles, quartiles, range, and interquartile range',
        'Analysing and interpreting cumulative frequency diagrams',
      ],
    },
    {
      category: 'Graphs of Linear Equations',
      items: [
        'Graphs of linear equations in two variables of the form ax + by = c',
      ],
    },
    {
      category: 'Simultaneous Linear Equations',
      items: [
        'Solving simultaneous linear equations in two variables by substitution and elimination',
        'Solving simultaneous linear equations graphically',
        'Formulating a pair of linear equations in two variables to solve problems',
      ],
    },
  ],
  4: [
    {
      category: 'Fractional Equations',
      items: [
        'Solving simple fractional equations that can be reduced to linear equations',
      ],
    },
    {
      category: 'Graphs of Quadratic Functions',
      items: [
        'Quadratic functions of the form y = ax² + bx + c',
        'Graphs of quadratic functions and their properties',
        'Positive or negative coefficient of x², maximum and minimum points, and symmetry',
      ],
    },
    {
      category: 'Quadratic Equations',
      items: [
        'Solving quadratic equations in one variable using the quadratic formula',
        'Formulating a quadratic equation in one variable to solve problems',
      ],
    },
    {
      category: 'Trigonometry',
      items: [
        'Using sine, cosine, and tangent of acute angles in right-angled triangles',
        'Finding unknown sides and angles, including angles of elevation and depression',
      ],
    },
    {
      category: 'Revision of Sec 1–3 G1 content',
      items: [
        'Numbers, Algebra, and Functions',
        'Geometry and Mensuration',
        'Statistics, Probability, and Data Handling',
      ],
    },
    {
      category: 'Exam Application & Revision',
      items: [
        'Interpreting exam-style word problems',
        'Prioritising marks through question strategy',
        'Timed practice and confidence under pressure',
      ],
    },
  ],
};

/**
 * MOE G2 Mathematics topics by secondary level (Sec 1–4), as chapter + items.
 * Used only for Secondary 1–4 G2 stream pages. Update when syllabus changes.
 */
export const G2_TOPICS_BY_LEVEL: Record<number, SyllabusTopic[]> = {
  1: [
    {
      category:
        'Chapter 1: Primes, Highest Common Factor and Lowest Common Multiple',
      items: [
        'Prime numbers',
        'Square roots and cube roots',
        'Highest common factor and lowest common multiple',
      ],
    },
    {
      category: 'Chapter 2: Integers, Rational Numbers and Real Numbers',
      items: [
        'Negative numbers',
        'Addition and subtraction involving negative integers',
        'Multiplication, division and combined operations involving negative integers',
        'Fractions and mixed numbers',
        'Decimals',
        'Rational, irrational and real numbers',
      ],
    },
    {
      category: 'Chapter 3: Approximation and Estimation',
      items: [
        'Rounding and significant figures',
        'Approximation and approximation errors in real-world contexts',
        'Estimation and estimation errors in real-world contexts',
      ],
    },
    {
      category: 'Chapter 4: Basic Algebra and Algebraic Manipulation',
      items: [
        'Basic algebraic concepts and notations',
        'Addition and subtraction of linear terms',
        'Expansion of linear expressions',
      ],
    },
    {
      category: 'Chapter 5: Linear Equations',
      items: [
        'Linear equations',
        'Applications of linear equations in real-world contexts',
        'Mathematical formulae',
      ],
    },
    {
      category: 'Chapter 6: Number Patterns',
      items: ['Number sequences', 'Number sequences and patterns'],
    },
    {
      category: 'Chapter 7: Percentage',
      items: [
        'Percentage',
        'Percentage change, percentage point and reverse percentage',
        'Percentage in real-world contexts',
      ],
    },
    {
      category: 'Chapter 8: Ratio and Rate',
      items: ['Ratio', 'Rate', 'Speed'],
    },
    {
      category: 'Chapter 9: Angles and Triangles',
      items: [
        'Properties of angles formed by intersecting lines',
        'Properties of angles formed by two parallel lines and transversal',
        'Triangles',
      ],
    },
    {
      category: 'Chapter 10: Perimeter and Area of Plane Figures',
      items: [
        'Perimeter and area of basic plane figures',
        'Perimeter and area of parallelograms',
        'Perimeter and area of trapeziums',
      ],
    },
    {
      category: 'Chapter 11: Volume and Surface Area of Prisms and Cylinders',
      items: [
        'Volume and surface area of cubes and cuboids',
        'Volume and surface area of prisms',
        'Volume and surface area of cylinders',
        'Volume and surface area of composite solids',
      ],
    },
    {
      category: 'Chapter 12: Statistical Data Handling',
      items: [
        'Pictogram',
        'Bar graph',
        'Pie chart',
        'Line graph',
        'Evaluation of statistics',
      ],
    },
  ],
  2: [
    {
      category:
        'Chapter 1: Linear Expressions, Linear Equations and Simple Inequalities',
      items: [
        'Linear expressions',
        'Linear expressions with fractional coefficients',
        'Linear equations with fractional coefficients and fractional equations',
        'Solving simple linear inequalities',
      ],
    },
    {
      category: 'Chapter 2: Linear Functions and Graphs',
      items: [
        'Cartesian coordinates',
        'Linear functions',
        'Applications of linear graphs in real-world contexts',
      ],
    },
    {
      category: 'Chapter 3: Simultaneous Linear Equations',
      items: [
        'Graphs of linear equations in the form ax + by = k',
        'Solving simultaneous linear equations using graphical method',
        'Solving simultaneous linear equations using algebraic methods',
        'Applications of simultaneous equations in real-world contexts',
      ],
    },
    {
      category:
        'Chapter 4: Expansion and Factorisation of Algebraic Expressions',
      items: [
        'Addition and subtraction of quadratic expressions',
        'Expansion of algebraic expressions of the form (a + b)(c + d)',
        'Expansion of quadratic expressions',
        'Factorisation of linear expressions',
        'Factorisation of quadratic expressions',
      ],
    },
    {
      category:
        'Chapter 5: Expansion and Factorisation using Special Algebraic Identities',
      items: [
        'Expansion using special algebraic identities',
        'Factorisation using special algebraic identities',
      ],
    },
    {
      category: 'Chapter 6: Algebraic Fractions',
      items: [
        'Algebraic fractions',
        'Multiplication and division of algebraic fractions',
      ],
    },
    {
      category: 'Chapter 7: Direct and Inverse Proportions',
      items: [
        'Direct proportion',
        'Algebraic and graphical representations of direct proportion',
        'Other forms of direct proportion',
        'Inverse proportion',
        'Algebraic and graphical representations of inverse proportion',
        'Other forms of inverse proportion',
      ],
    },
    {
      category: 'Chapter 8: Polygons and Geometrical Constructions',
      items: [
        'Quadrilaterals',
        'Geometrical constructions of triangles and quadrilaterals',
        'Polygons',
      ],
    },
    {
      category: 'Chapter 9: Congruence and Similarity',
      items: [
        'Congruent figures',
        'Similar figures',
        'Similarity and map scales',
      ],
    },
    {
      category: "Chapter 10: Pythagoras' Theorem",
      items: [
        "Pythagoras' Theorem",
        "Applications of Pythagoras' Theorem in real-world contexts",
        "Converse of Pythagoras' Theorem",
      ],
    },
    {
      category:
        'Chapter 11: Volume and Surface Area of Pyramids, Cones and Spheres',
      items: [
        'Volume and surface area of pyramids',
        'Volume and surface area of cones',
        'Volume and surface area of spheres',
        'Volume and surface area of composite solids',
      ],
    },
    {
      category: 'Chapter 12: Probability of Single Events',
      items: [
        'Probability experiment and sample space',
        'Probability of single events',
        'Further examples of probability of single events',
      ],
    },
    {
      category: 'Chapter 13: Statistical Diagrams',
      items: [
        'Dot diagrams',
        'Histograms for ungrouped data',
        'Stem-and-leaf diagrams',
        'Histograms for grouped data',
      ],
    },
    {
      category: 'Chapter 14: Averages of Statistical Data',
      items: ['Mean', 'Median', 'Mode', 'Measures of central tendency'],
    },
  ],
  3: [
    {
      category: 'Chapter 1: Algebraic Expressions and Formulae',
      items: [
        'Expansion of algebraic expressions (Recap)',
        'Factorisation of algebraic expressions into the form (a + b)(c + d)',
        'Addition and subtraction of algebraic fractions',
        'Solving equations involving algebraic fractions',
        'Manipulation of algebraic formulae',
      ],
    },
    {
      category: 'Chapter 2: Quadratic Equations and Graphs',
      items: [
        'Solving quadratic equations by factorisation',
        'Solving quadratic equations by completing the square',
        'Solving quadratic equations using formula',
        'Solving fractional equations reducible to quadratic equations',
        'Solving quadratic equations by graphical method',
        'Applications of quadratic equations in mathematical problems and in real-world contexts',
      ],
    },
    {
      category: 'Chapter 3: Indices and Standard Form',
      items: [
        'Indices',
        'Laws of indices',
        'Zero and negative indices',
        'Rational indices',
        'Compound interest',
        'Standard form',
      ],
    },
    {
      category: 'Chapter 4: Coordinate Geometry',
      items: [
        'Length of a line segment',
        'Gradient of a straight line',
        'Equation of a straight line',
      ],
    },
    {
      category: 'Chapter 5: Graphs of Functions and Graphical Solution',
      items: [
        'Graphs of cubic functions',
        'Graphs of reciprocal functions',
        'Graphs of exponential functions',
        'Gradient of a curve',
        'Applications of graphs in real-world contexts',
      ],
    },
    {
      category: 'Chapter 6: Trigonometry',
      items: [
        'Trigonometric ratios',
        'Applications of trigonometric ratios to find unknown sides of right-angled triangles',
        'Applications of trigonometric ratios to find unknown angles in right-angled triangles',
        'Applications of trigonometric ratios in real-world contexts',
      ],
    },
    {
      category: 'Chapter 7: Further Trigonometry',
      items: [
        'Sine and cosine of obtuse angles',
        'Area of triangle',
        'Sine Rule',
        'Cosine Rule',
      ],
    },
    {
      category: 'Chapter 8: Congruence and Similarity',
      items: [
        'Congruence and similarity',
        'Similarity and enlargement',
        'Bisectors and scale drawings',
      ],
    },
  ],
  4: [
    {
      category: 'Chapter 1: Applications of Trigonometry',
      items: [
        'Angles of elevation and depression',
        'Bearings',
        'Three-dimensional problems',
      ],
    },
    {
      category: 'Chapter 2: Arc Length, Area of Sector and Radian Measure',
      items: [
        'Arc length',
        'Area of a sector',
        'Radian measure',
        'Arc length and area of a sector using radian measure',
      ],
    },
    {
      category: 'Chapter 3: Probability of Combined Events',
      items: [
        'Probability of a single event',
        'Probability of combined events',
        'Addition Law of Probability and mutually exclusive events',
        'Multiplication Law of Probability and independent events',
      ],
    },
    {
      category: 'Chapter 4: Cumulative Frequency',
      items: [
        'Cumulative frequency table and curve',
        'Median, quartiles, percentiles, range and interquartile range',
        'Box-and-whisker plots',
      ],
    },
    {
      category: 'Chapter 5: Mean and Standard Deviation',
      items: ['Mean', 'Standard deviation'],
    },
    {
      category: 'Chapter 6: Geometrical Properties of Circles',
      items: ['Symmetric properties of circles', 'Angle properties of circles'],
    },
    {
      category: 'Chapter 7: Revision – Numbers and Algebra',
      items: [
        'Numbers and percentages',
        'Proportion, ratio, rate and speed',
        'Algebraic manipulation and formulae',
        'Equations and inequalities',
        'Functions and graphs',
        'Graphs in practical situations',
      ],
    },
    {
      category: 'Chapter 8: Revision – Geometry and Measurement',
      items: [
        'Angles, triangles and polygons',
        'Congruence and similarity',
        "Pythagoras' Theorem and trigonometry",
        'Mensuration',
        'Coordinate geometry',
        'Properties of circles',
      ],
    },
    {
      category: 'Chapter 9: Revision – Probability and Statistics',
      items: ['Probability', 'Statistics'],
    },
  ],
};

/**
 * MOE G3 Mathematics topics by secondary level (Sec 1–4), as chapter + items.
 * Used only for Secondary 1–4 G3 stream pages. Update when syllabus changes.
 */
export const G3_TOPICS_BY_LEVEL: Record<number, SyllabusTopic[]> = {
  1: [
    {
      category:
        'Chapter 1: Primes, Highest Common Factor and Lowest Common Multiple',
      items: [
        'Prime numbers',
        'Square roots and cube roots',
        'Highest common factor and lowest common multiple',
      ],
    },
    {
      category: 'Chapter 2: Integers, Rational Numbers and Real Numbers',
      items: [
        'Negative numbers',
        'Addition and subtraction involving negative integers',
        'Multiplication, division and combined operations involving negative integers',
        'Fractions and mixed numbers',
        'Decimals',
        'Rational, irrational and real numbers',
      ],
    },
    {
      category: 'Chapter 3: Approximation and Estimation',
      items: [
        'Rounding and significant figures',
        'Approximation and approximation errors in real-world contexts',
        'Estimation and estimation errors in real-world contexts',
      ],
    },
    {
      category: 'Chapter 4: Basic Algebra and Algebraic Manipulation',
      items: [
        'Basic algebraic concepts and notations',
        'Addition and subtraction of linear terms',
        'Expansion and factorisation of linear expressions',
        'Linear expressions with fractional coefficients',
      ],
    },
    {
      category: 'Chapter 5: Linear Equations',
      items: [
        'Linear equations',
        'Linear equations with fractional coefficients and fractional equations',
        'Applications of linear equations in real-world contexts',
        'Mathematical formulae',
      ],
    },
    {
      category: 'Chapter 6: Linear Functions and Graphs',
      items: [
        'Cartesian coordinates',
        'Functions',
        'Linear functions',
        'Applications of linear graphs in real-world contexts',
      ],
    },
    {
      category: 'Chapter 7: Number Patterns',
      items: ['Number sequences', 'Number sequences and patterns'],
    },
    {
      category: 'Chapter 8: Percentage',
      items: [
        'Percentage',
        'Percentage change, percentage point and reverse percentage',
        'Percentage in real-world contexts',
      ],
    },
    {
      category: 'Chapter 9: Ratio and Rate',
      items: ['Ratio', 'Rate', 'Speed'],
    },
    {
      category: 'Chapter 10: Basic Geometry',
      items: [
        'Basic geometrical concepts and notations',
        'Properties of angles formed by intersecting lines',
        'Properties of angles formed by two parallel lines and a transversal',
      ],
    },
    {
      category: 'Chapter 11: Polygons and Geometrical Constructions',
      items: [
        'Triangles',
        'Quadrilaterals',
        'Geometrical constructions: triangles and quadrilaterals',
        'Polygons',
      ],
    },
    {
      category: 'Chapter 12: Perimeter and Area of Plane Figures',
      items: [
        'Conversion of units',
        'Perimeter and area of basic plane figures',
        'Perimeter and area of parallelograms',
        'Perimeter and area of trapeziums',
      ],
    },
    {
      category: 'Chapter 13: Volume and Surface Area of Prisms and Cylinders',
      items: [
        'Conversion of units',
        'Three-dimensional solids',
        'Volume and surface area of cubes and cuboids',
        'Volume and surface area of prisms',
        'Volume and surface area of cylinders',
        'Volume and surface area of composite solids',
      ],
    },
    {
      category: 'Chapter 14: Statistical Data Handling',
      items: [
        'Frequency table',
        'Pictogram',
        'Bar graph',
        'Pie chart',
        'Line graph',
        'Evaluation of statistical representations',
        'Statistical investigation',
      ],
    },
  ],
  2: [
    {
      category: 'Chapter 1: Linear Graphs and Simultaneous Linear Equations',
      items: [
        'Equations of straight lines',
        'Graphs of linear equations in the form ax + by = c',
        'Solving simultaneous linear equations using graphical method',
        'Solving simultaneous linear equations using algebraic methods',
        'Applications of simultaneous linear equations in real-world contexts',
      ],
    },
    {
      category: 'Chapter 2: Linear Inequalities',
      items: [
        'Simple inequalities',
        'Solving simple linear inequalities',
        'Solving problems involving linear inequalities',
      ],
    },
    {
      category:
        'Chapter 3: Expansion and Factorisation of Algebraic Expressions',
      items: [
        'Addition and subtraction of quadratic expressions',
        'Expansion of algebraic expressions in the form (a + b)(c + d)',
        'Expansion of quadratic expressions',
        'Factorisation of quadratic expressions',
        'Factorisation of algebraic expressions into the form (a + b)(c + d)',
      ],
    },
    {
      category:
        'Chapter 4: Expansion and Factorisation Using Special Algebraic Identities',
      items: [
        'Expansion using special algebraic identities',
        'Factorisation using special algebraic identities',
      ],
    },
    {
      category: 'Chapter 5: Quadratic Equations and Graphs',
      items: [
        'Solving quadratic equations by factorisation',
        'Quadratic functions and graphs',
      ],
    },
    {
      category: 'Chapter 6: Algebraic Fractions and Formulae',
      items: [
        'Algebraic fractions',
        'Multiplication and division of algebraic fractions',
        'Addition and subtraction of algebraic fractions',
        'Solving equations involving algebraic fractions',
        'Manipulation of algebraic formulae',
      ],
    },
    {
      category: 'Chapter 7: Direct and Inverse Proportions',
      items: [
        'Direct proportion',
        'Algebraic and graphical representations of direct proportion',
        'Other forms of direct proportion',
        'Inverse proportion',
        'Algebraic and graphical representations of inverse proportion',
        'Other forms of inverse proportion',
      ],
    },
    {
      category: 'Chapter 8: Congruence and Similarity',
      items: [
        'Congruent figures',
        'Similar figures',
        'Similarity and enlargement',
      ],
    },
    {
      category: "Chapter 9: Pythagoras' Theorem",
      items: [
        "Pythagoras' Theorem",
        "Applications of Pythagoras' Theorem in real-world contexts",
        "Converse of Pythagoras' Theorem",
      ],
    },
    {
      category: 'Chapter 10: Trigonometric Ratios',
      items: [
        'Trigonometric ratios',
        'Applications of trigonometric ratios to find unknown sides of right-angled triangles',
        'Applications of trigonometric ratios to find unknown angles in right-angled triangles',
        'Applications of trigonometric ratios in real-world contexts',
      ],
    },
    {
      category:
        'Chapter 11: Volume and Surface Area of Pyramids, Cones and Spheres',
      items: [
        'Volume and surface area of pyramids',
        'Volume and surface area of cones',
        'Volume and surface area of spheres',
        'Volume and surface area of composite solids',
      ],
    },
    {
      category: 'Chapter 12: Probability of Single Events',
      items: [
        'Probability experiment and sample space',
        'Probability of single events',
        'Further examples of probability of single events',
        'Experimental approach to finding probability',
      ],
    },
    {
      category: 'Chapter 13: Statistical Diagrams',
      items: [
        'Dot diagrams',
        'Histograms for ungrouped data',
        'Stem-and-leaf diagrams',
        'Histograms for grouped data',
      ],
    },
    {
      category: 'Chapter 14: Averages of Statistical Data',
      items: ['Mean', 'Median', 'Mode', 'Measures of central tendency'],
    },
  ],
  3: [
    {
      category: 'Chapter 1: Quadratic and Fractional Equations',
      items: [
        'Solving quadratic equations by completing the square',
        'Solving quadratic equations using formula',
        'Solving fractional equations reducible to quadratic equations',
        'Solving quadratic equations by graphical method',
        'Sketching graphs of quadratic functions',
        'Applications of quadratic equations and functions in real-world contexts',
      ],
    },
    {
      category: 'Chapter 2: Simultaneous Linear Inequalities',
      items: [
        'Solving simultaneous linear inequalities',
        'Solving problems involving simultaneous linear inequalities',
      ],
    },
    {
      category: 'Chapter 3: Indices and Standard Form',
      items: [
        'Indices',
        'Laws of indices',
        'Zero and negative indices',
        'Rational indices',
        'Compound interest',
        'Standard form',
      ],
    },
    {
      category: 'Chapter 4: Coordinate Geometry',
      items: [
        'Length of a line segment',
        'Gradient of a straight line',
        'Equation of a straight line',
      ],
    },
    {
      category: 'Chapter 5: Graphs of Functions and Graphical Solution',
      items: [
        'Graphs of cubic functions',
        'Graphs of reciprocal functions',
        'Graphs of exponential functions',
        'Gradient of a curve',
        'Applications of graphs in real-world contexts',
      ],
    },
    {
      category: 'Chapter 6: Further Trigonometry',
      items: [
        'Sine and cosine of obtuse angles',
        'Area of triangle',
        'Sine Rule',
        'Cosine Rule',
      ],
    },
    {
      category: 'Chapter 7: Applications of Trigonometry',
      items: [
        'Angles of elevation and depression',
        'Bearings',
        'Three-dimensional problems',
      ],
    },
    {
      category: 'Chapter 8: Arc Length, Sector Area and Radian Measure',
      items: [
        'Length of arc',
        'Area of sector',
        'Radian measure',
        'Arc length and sector area using radian measure',
      ],
    },
    {
      category: 'Chapter 9: Congruence and Similarity Tests',
      items: [
        'Bisectors and scale drawings',
        'Congruence tests',
        'Similarity tests',
        'Applications of congruent and similar triangles',
      ],
    },
    {
      category: 'Chapter 10: Area and Volume of Similar Figures and Solids',
      items: [
        'Area of similar figures',
        'Volume of similar solids',
        'Solving problems involving similar solids',
      ],
    },
    {
      category: 'Chapter 11: Geometrical Properties of Circles',
      items: ['Symmetric properties of circles', 'Angle properties of circles'],
    },
  ],
  4: [
    {
      category: 'Chapter 1: Sets',
      items: [
        'Sets and set notations',
        'Venn diagrams, universal sets, complements of sets and subsets',
        'Intersection of two sets',
        'Union of two sets',
        'Solving problems involving sets',
      ],
    },
    {
      category: 'Chapter 2: Probability of Combined Events',
      items: [
        'Probability of a single event',
        'Probability of combined events',
        'Addition Law of Probability and mutually exclusive events',
        'Multiplication Law of Probability and independent events',
      ],
    },
    {
      category: 'Chapter 3: Statistical Data Analysis',
      items: [
        'Cumulative frequency table and curve',
        'Median, quartiles, percentiles, range and interquartile range',
        'Box-and-whisker plots',
        'Standard deviation',
      ],
    },
    {
      category: 'Chapter 4: Matrices',
      items: [
        'Introduction to matrices',
        'Addition and subtraction of matrices',
        'Matrix multiplication',
        'Applications of matrices in real-world contexts',
      ],
    },
    {
      category: 'Chapter 5: Vectors',
      items: [
        'Vectors in two dimensions',
        'Addition of vectors',
        'Vector subtraction',
        'Scalar multiples of a vector',
        'Expression of a vector in terms of two other vectors',
        'Position vectors',
        'Applications of vectors',
      ],
    },
    {
      category: 'Chapter 6: Revision – Numbers and Algebra',
      items: [
        'Numbers and percentages',
        'Proportion, ratio, rate and speed',
        'Algebraic manipulation and formulae',
        'Equations and inequalities',
        'Functions and graphs',
        'Graphs in practical situations',
        'Sets',
        'Matrices',
      ],
    },
    {
      category: 'Chapter 7: Revision – Geometry and Measurement',
      items: [
        'Angles, triangles and polygons',
        'Congruence and similarity',
        "Pythagoras' Theorem and trigonometry",
        'Mensuration',
        'Coordinate geometry',
        'Vectors',
        'Properties of circles',
      ],
    },
    {
      category: 'Chapter 8: Revision – Probability and Statistics',
      items: ['Probability', 'Statistics'],
    },
  ],
};

/**
 * IP Mathematics topics by secondary level (Sec 1–4).
 *
 * IP curricula vary by school — each IP school designs its own scheme of work.
 * The progression is: Sec 1–2 covers G3 / O-Level E-Math content at an
 * accelerated pace with enrichment; Sec 3–4 covers A-Math content plus early
 * H2 Math topics, preparing students for JC and A-Level H2 Math without
 * sitting O-Levels or the Singapore-Cambridge SEC.
 *
 * Sec 1–4 are modelled on Hwa Chong Institution's IP Mathematics schemes of
 * work (Sec 1: 2015 SOW, Sec 2 IP/SBGE: 2012 SOW, Sec 3 & 4: 2015 SOW),
 * which are representative of the top-band IP curriculum. Other IP schools
 * (RI, NJC, Dunman High, ACSI, etc.) follow broadly similar progressions
 * but with school-specific sequencing and emphasis.
 *
 * Update when schools publish revised schemes of work or when the H2 Math
 * syllabus (9758) changes — IP schools align their Sec 3–4 content to the
 * H2 pathway.
 */
export const IP_TOPICS_BY_LEVEL: Record<number, SyllabusTopic[]> = {
  1: [
    {
      category: 'Arithmetic I',
      items: [
        'Prime numbers, HCF and LCM',
        'Integers, rational numbers and real numbers',
        'Approximation and estimation',
      ],
    },
    {
      category: 'Algebra I',
      items: [
        'Basic algebra and algebraic manipulation',
        'Linear equations, algebraic fractions and simple inequalities',
        'Applications of mathematics in practical situations',
        'Ratio, rate and speed',
      ],
    },
    {
      category: 'Coordinate Geometry I',
      items: ['Functions and graphs (linear and quadratic)'],
    },
    {
      category: 'Statistics I',
      items: ['Statistical data handling', 'Averages of statistical data'],
    },
    {
      category: 'Geometry I',
      items: [
        'Basic geometry',
        'Triangles, quadrilaterals and polygons',
        'Geometrical constructions',
        'Perimeter and area of plane figures',
        'Volume and surface area of solids',
      ],
    },
    {
      category: 'Trigonometry I',
      items: ["Pythagoras' theorem"],
    },
    {
      category: 'Sets',
      items: ['Set notations', 'Venn diagrams'],
    },
  ],
  2: [
    {
      category: 'Algebra II',
      items: [
        'Monomials and polynomials',
        'Expansion and factorisation',
        'Quadratic equations',
        'Algebraic manipulation',
      ],
    },
    {
      category: 'Algebra III',
      items: [
        'Further indices and surds',
        'Coordinate geometry and linear graphs',
        'Simultaneous linear equations',
      ],
    },
    {
      category: 'Arithmetic II',
      items: ['Arithmetic problem solving', 'Financial literacy and budgeting'],
    },
    {
      category: 'Trigonometry II',
      items: [
        "Pythagoras' theorem",
        'Numerical trigonometry',
        'Angles of elevation and depression',
      ],
    },
    {
      category: 'Geometry II',
      items: [
        'Congruency and similarity',
        'Geometrical properties of circles',
        'Plane geometry and proofs',
      ],
    },
    {
      category: 'Statistics II',
      items: [
        'Mean and standard deviation',
        'Distribution of data (cumulative frequency, box-and-whisker plots, histograms)',
        'Higher statistics (correlation, lines of best fit, hypothesis testing)',
      ],
    },
  ],
  3: [
    {
      category: 'Algebra II',
      items: [
        'Advanced algebraic manipulation',
        'Surds and indices',
        'Equations and inequalities',
        'Logarithms',
        'Polynomials, factor and remainder theorems',
        'Partial fractions',
      ],
    },
    {
      category: 'Relations and Functions I',
      items: [
        'Relations, functions and modulus functions',
        'Power functions and standard graphs',
        'Exponential and logarithmic functions',
        'Parabolas and circles',
      ],
    },
    {
      category: 'Coordinate Geometry II',
      items: [
        'Points, lines and slopes',
        'Applications of straight line graphs',
      ],
    },
    {
      category: 'Trigonometry III',
      items: [
        'Further trigonometry',
        'Trigonometric functions',
        'Trigonometric identities and equations',
      ],
    },
    {
      category: 'Matrices',
      items: ['Introduction to matrices and applications'],
    },
  ],
  4: [
    {
      category: 'Trigonometry IV',
      items: [
        'Further trigonometric identities',
        'Mensuration: arc length, sector area and radian measure',
        'Binomial theorem',
      ],
    },
    {
      category: 'Calculus I',
      items: [
        'Differentiation from first principles and rules of differentiation',
        'Tangents, normals and rates of change',
        'Maxima, minima and stationary points',
        'Integration techniques and definite integrals',
        'Applications of integration: area of a region',
        'Applications of integration: kinematics',
      ],
    },
    {
      category: 'Probability',
      items: [
        'Independent and dependent events',
        'Mutually exclusive events',
        'Conditional probability and tree diagrams',
      ],
    },
    {
      category: 'Vectors I',
      items: [
        'Vectors in two dimensions',
        'Position vectors and magnitude',
        'Operations on vectors: addition, subtraction, scalar multiplication',
      ],
    },
  ],
};

/**
 * Per-level context for secondary stream pages (Sec 1–4).
 * Provides level-specific framing so that pages within the same band
 * (e.g., Sec 1 G2 vs Sec 2 G2) are not duplicates. Each level has a
 * distinct educational milestone that shapes the messaging.
 *
 * `heroSuffix` is the default suffix for G1/G2/G3 students who sit the
 * national leaving exam (O-Level today, Singapore-Cambridge SEC from
 * 2027). `ipHeroSuffix` overrides it for IP students, who bypass the
 * national exam and progress directly to JC for A-Level H2 Math.
 */
export const secondaryLevelContext: Record<
  number,
  {
    milestone: string;
    heroSuffix: string;
    ipHeroSuffix: string;
    challengeTitle: string;
    challengeDescription: string;
    supportTitle: string;
    supportDescription: string;
  }
> = {
  1: {
    milestone: 'transition year',
    heroSuffix:
      'Secondary 1 is a transition year where students adjust to faster-paced, more abstract mathematical thinking after primary school. Under Full Subject-Based Banding, these students will sit the Singapore-Cambridge SEC exam — a single national exam replacing O-Levels, N(A)-Levels, and N(T)-Levels — with Mathematics taken at their G1, G2, or G3 level.',
    ipHeroSuffix:
      'Secondary 1 is the start of the six-year Integrated Programme. IP students bypass the national leaving exam (O-Level today, Singapore-Cambridge SEC from 2027) and progress directly through Sec 1–4 into JC for A-Level H2 Math. Sec 1 builds the foundation for the A-Math depth that lands in Sec 3 and the calculus and vectors introduced in Sec 4.',
    challengeTitle: 'Adjusting to secondary-level expectations',
    challengeDescription:
      'The jump from primary to secondary Math is significant. Students encounter formal algebra, negative numbers, and multi-step reasoning for the first time, often alongside a new school environment and heavier workload.',
    supportTitle: 'Smooth the primary-to-secondary transition',
    supportDescription:
      'We bridge the gap by connecting new concepts to familiar primary-school methods, building confidence before pushing further into abstract territory.',
  },
  2: {
    milestone: 'consolidation year',
    heroSuffix:
      'Secondary 2 is a consolidation year where gaps from Sec 1 compound and students must solidify foundations before Upper Secondary. These students will sit the Singapore-Cambridge SEC exam — a single national exam that replaces all three former secondary leaving exams (O-Level, N(A)-Level, N(T)-Level).',
    ipHeroSuffix:
      'Secondary 2 is the bridging year of the Integrated Programme. IP students bypass the national leaving exam and continue directly toward JC and A-Level H2 Math. Foundations from Sec 1 are consolidated and extended into algebraic depth, plane geometry proofs, and statistics, ahead of the sharp A-Math content jump in Sec 3.',
    challengeTitle: 'Gaps from Sec 1 compounding quietly',
    challengeDescription:
      'Topics introduced in Sec 1 are now assumed knowledge. Students who moved on without fully understanding algebra, ratios, or equation-solving find that these gaps make every new topic harder.',
    supportTitle: 'Close Sec 1 gaps before they widen',
    supportDescription:
      'Diagnostics identify exactly which Sec 1 foundations are shaky, and we reinforce them alongside current topics so students enter Upper Secondary on solid ground.',
  },
  3: {
    milestone: 'content jump year',
    heroSuffix:
      'Secondary 3 marks a sharp content jump as the Upper Secondary syllabus introduces more demanding topics and preparation for the SEC exam begins. Current Sec 3 students are the first cohort sitting the Singapore-Cambridge SEC in 2027 — a single national exam replacing all three former leaving exams (O-Level, N(A)-Level, N(T)-Level).',
    ipHeroSuffix:
      'Secondary 3 marks the sharp content jump in the Integrated Programme — A-Math depth arrives in full, with logarithms, polynomials, partial fractions, modulus and exponential functions, further trigonometry, and matrices. IP students bypass the national leaving exam and continue toward JC and A-Level H2 Math.',
    challengeTitle: 'Managing the Upper Secondary content jump',
    challengeDescription:
      'The syllabus expands significantly in Sec 3, with new topics like trigonometry, advanced algebra, and (for some streams) early calculus. Students who were comfortable before may suddenly feel stretched.',
    supportTitle: 'Build momentum through the content jump',
    supportDescription:
      'We front-load the hardest new concepts with structured teaching and targeted practice, so students keep pace instead of falling behind as complexity increases.',
  },
  4: {
    milestone: 'exam year',
    heroSuffix:
      'Secondary 4 is the exam year where revision strategy, time management, and consistent execution under pressure determine final results. Current Sec 4 students sit the O-Level or N-Level examinations under the Express, Normal Academic (NA), and Normal Technical (NT) streams.',
    ipHeroSuffix:
      'Secondary 4 is the pre-JC year of the Integrated Programme — calculus, vectors, and probability are introduced, bridging directly into JC and A-Level H2 Math. IP students do not sit the O-Level or SEC; school-internal assessments shape progression into JC1.',
    challengeTitle: 'Balancing revision breadth with exam-year pressure',
    challengeDescription:
      'Students must revise a full syllabus while managing multiple subjects, mock exams, and increasing anxiety. Without a clear revision plan, important topics are often under-practised.',
    supportTitle: 'Structured revision for exam-year performance',
    supportDescription:
      'We prioritise high-impact topics, build exam stamina through timed practice, and track revision coverage so nothing critical is left unaddressed before the exam.',
  },
};
